import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Gamepad2, 
  Mail, 
  Lock, 
  ArrowLeft,
  LogIn,
  Eye,
  EyeOff
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Matrix Rain Effect Component - Casa de Papel Theme
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€$£¥₿#@&%?!';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        const brightness = Math.random();
        if (brightness > 0.95) {
          ctx.fillStyle = '#ffffff';
        } else if (brightness > 0.7) {
          ctx.fillStyle = '#ff3333';
        } else {
          ctx.fillStyle = '#8b0000';
        }
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-15"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

const GameLoginPage = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('game_token');
    if (token) {
      checkToken(token);
    }
  }, []);

  const checkToken = async (token) => {
    try {
      await api.get(`/game/players/me?token=${token}`);
      // Always redirect to player dashboard so they can choose what to do
      navigate('/mon-espace-joueur');
    } catch (e) {
      localStorage.removeItem('game_token');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/game/players/login', formData);
      localStorage.setItem('game_token', res.data.token);
      toast.success(`Bienvenue ${res.data.player.first_name} !`);
      
      // Always redirect to player dashboard so they can choose what to do
      navigate('/mon-espace-joueur');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      <MatrixRain />
      <div className="fixed inset-0 bg-gradient-to-b from-red-900/20 via-black/30 to-red-900/20 pointer-events-none z-0" />
      
      <div className="w-full max-w-md relative z-10">
        <Link 
          to={gameId ? `/jeu/${gameId}` : '/le-coffre-msc'} 
          className="inline-flex items-center gap-2 text-red-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </Link>

        <Card className="bg-black border-2 border-red-500/50 shadow-xl shadow-red-500/20">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/40">
              <Gamepad2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">Connexion Joueur</CardTitle>
            <CardDescription className="text-white/60">
              Connectez-vous pour participer au Coffre MSC
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500/50" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="pl-10 bg-red-950/50 border-red-500/30 text-white placeholder:text-red-500/30"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500/50" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pl-10 pr-10 bg-red-950/50 border-red-500/30 text-white placeholder:text-red-500/30"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500/50 hover:text-red-400 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/30"
                disabled={loading}
              >
                {loading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2" /> Connexion...</>
                ) : (
                  <><LogIn className="w-4 h-4 mr-2" /> Se connecter</>
                )}
              </Button>

              <p className="text-center text-white/60 text-sm">
                Pas encore inscrit ?{' '}
                <Link 
                  to={gameId ? `/jeu/${gameId}/inscription` : '/le-coffre-msc/inscription'} 
                  className="text-red-400 hover:underline"
                >
                  Créer un compte
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GameLoginPage;
